// ─── Pastille flottante « Mise en Pratique en cours » (Core) ──────
// Porté de MoPCal. Montée AU NIVEAU APP (au-dessus de la coquille) : l'exercice
// reste visible pendant que l'utilisateur navigue dans l'app.
//   • déployée → carte : consignes cochables « Fait », indice, Terminer ;
//   • réduite  → pastille 🎓 n/total (tap = redéployer).
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { usePratique } from './PratiqueContext';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function BarrePratique() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const insets = useSafeAreaInsets();
  const p = usePratique();
  const [saving, setSaving] = useState(false);

  if (!p?.actif) return null;
  const { actif, deploye, reduire, agrandir, fermer, cocher } = p;
  const mep = actif.mep;
  const tr = (v) => (typeof v === 'string' ? t(v) : v);
  const etapes = mep.etapes || [];
  const faites = etapes.filter((_, i) => actif.faites[i]).length;
  const tout = etapes.length > 0 && faites === etapes.length;
  const bas = insets.bottom + 96;   // au-dessus barre d'onglets + pied

  async function terminer() {
    setSaving(true);
    try { await p.terminerActif(); } finally { setSaving(false); }
  }

  if (!deploye) {
    return (
      <TouchableOpacity style={[s.pastille, { bottom: bas }]} onPress={agrandir} activeOpacity={0.85}
        accessibilityLabel={`${tr(mep.titre)} · ${t('apprentissage.enCours')}`}>
        <Text style={s.pastilleTxt}>🎓 {faites}/{etapes.length}</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={[s.carte, { bottom: bas }]}>
      <View style={s.tete}>
        <Text style={s.emoji}>{mep.emoji || '🎓'}</Text>
        <View style={{ flex: 1 }}>
          <Text style={s.titre} numberOfLines={1}>{tr(mep.titre)}</Text>
          {!!mep.sousTitre && <Text style={s.sous} numberOfLines={1}>{tr(mep.sousTitre)}</Text>}
        </View>
        <TouchableOpacity onPress={reduire} hitSlop={HIT} accessibilityLabel={t('apprentissage.enCours')}>
          <Text style={s.icTete}>⌄</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={fermer} hitSlop={HIT} accessibilityLabel={t('commun.fermer')}>
          <Text style={s.icTete}>✕</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={{ maxHeight: 260 }} showsVerticalScrollIndicator={false}>
        {etapes.map((e, i) => {
          const fait = !!actif.faites[i];
          return (
            <TouchableOpacity key={i} style={[s.etape, fait && s.etapeFaite]} onPress={() => cocher(i)} activeOpacity={0.7}>
              <Text style={[s.num, fait && s.numFait]}>{fait ? '✓' : i + 1}</Text>
              <View style={{ flex: 1 }}>
                <Text style={[s.consigne, fait && s.consigneFaite]}>{tr(e.consigne)}</Text>
                {!!e.indice && !fait && <Text style={s.indice}>💡 {tr(e.indice)}</Text>}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      <TouchableOpacity style={[s.terminer, !tout && s.terminerOff]} disabled={!tout || saving} onPress={terminer}>
        {saving ? <ActivityIndicator color="#fff" size="small" />
          : <Text style={s.terminerTxt}>{tout ? `✓ ${t('tuto.terminer')}` : `${faites}/${etapes.length}`}</Text>}
      </TouchableOpacity>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  pastille: { position: 'absolute', right: 12, backgroundColor: UI.accent, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 10, elevation: 6, shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 8, shadowOffset: { width: 0, height: 3 }, zIndex: 60 },
  pastilleTxt: { color: '#fff', fontSize: 14, fontWeight: '800' },
  carte: { position: 'absolute', left: 10, right: 10, backgroundColor: UI.card, borderRadius: 16, borderWidth: 1, borderColor: UI.border, padding: 12, elevation: 8, shadowColor: '#000', shadowOpacity: 0.28, shadowRadius: 12, shadowOffset: { width: 0, height: 4 }, zIndex: 60 },
  tete: { flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 8 },
  emoji: { fontSize: 20 },
  titre: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  icTete: { fontSize: 17, color: UI.accent, paddingHorizontal: 5 },
  etape: { flexDirection: 'row', gap: 10, paddingVertical: 8, paddingHorizontal: 4, alignItems: 'flex-start' },
  etapeFaite: { opacity: 0.6 },
  num: { width: 22, height: 22, borderRadius: 11, backgroundColor: UI.champ, color: UI.muted, fontSize: 12, fontWeight: '800', textAlign: 'center', lineHeight: 22, overflow: 'hidden' },
  numFait: { backgroundColor: UI.accent, color: '#fff' },
  consigne: { fontSize: 13, color: UI.ink, lineHeight: 18 },
  consigneFaite: { textDecorationLine: 'line-through', color: UI.muted },
  indice: { fontSize: 11.5, color: UI.faint, marginTop: 2, fontStyle: 'italic' },
  terminer: { marginTop: 8, backgroundColor: UI.accent, borderRadius: 11, paddingVertical: 10, alignItems: 'center' },
  terminerOff: { opacity: 0.45 },
  terminerTxt: { color: '#fff', fontSize: 13.5, fontWeight: '800' },
});
